"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="201603" className="hidden md:flex gap-8">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 bg-muted animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default text-[14px] font-medium tracking-tight text-foreground hover:text-secondary transition-colors duration-300">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-[12px]">
      <nav className="flex flex-col px-6 py-8 gap-6">
        {loading ? <div className="flex flex-col gap-6">
            {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted animate-pulse" />)}
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="font-default text-[14px] font-medium tracking-tight text-foreground hover:text-secondary transition-colors duration-300" onClick={() => setIsOpen(false)}>
              {item.label}
            </Link>)}
        <div className="pt-4 mt-2 border-t border-border/50">
          <Button className="w-full rounded-sm font-default text-[14px] font-medium tracking-tight h-12 bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-sm" asChild>
            <Link to="#" onClick={() => setIsOpen(false)}>Start Consultation</Link>
          </Button>
        </div>
      </nav>
    </div>;
}
export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ease-in-out ${isScrolled ? "bg-background/80 backdrop-blur-[12px] border-b border-border shadow-md" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 flex items-center">
          <Link to="#" className="flex items-center" aria-label="Briviox Home">
            <WvcLogo className="h-7 w-auto text-primary" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="16">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-6 flex-shrink-0">
          <Button className="hidden md:inline-flex rounded-sm font-default text-[14px] font-medium tracking-tight px-8 h-12 bg-primary text-primary-foreground hover:bg-primary/90 hover:ring-2 hover:ring-secondary/20 transition-all duration-300 shadow-sm" asChild>
            <Link to="#">Start Consultation</Link>
          </Button>

          <Button variant="ghost" size="icon" className="md:hidden text-primary" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-expanded={isMobileOpen} aria-label="Toggle navigation menu">
            {isMobileOpen ? <X className="size-6 stroke-[1.5]" /> : <Menu className="size-6 stroke-[1.5]" />}
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="16">
        <MobileMenu isOpen={isMobileOpen} setIsOpen={setIsMobileOpen} />
      </MenuProvider>
    </section>;
}